import React, { Component } from "react"
import PropTypes from "prop-types"
import {
  withRadium,
  Price,
  Specs,
  DetectCompact,
  SearchParams,
  gtmEvent,
  LocalVehicle
} from "higher-order-components"
import { StepCounter, Loading } from "components"
import { InlineDisclaimerBlock } from "containers"
import { Button, InputCheckBox, InfoIcon, StarIcon, Caption, Modal, Content, Interactive } from "AtomicComponents"
import { BrowserHeightSection, Disclaimer } from "AtomicContainers"
import { fromLinkInputs, formSetDefaultValues } from "AtomicUtils/formHandler"
import { childParser } from "containers/utilities"
import { noop, parseKoalaData, get, scrollToNode } from "utils"
import { gtmBuildToolActionTypes } from "store/gtm-events"
import exposeGlobal from "apis/expose-global"
import SingularityBar from "./singularity-bar"
import BuildCompactNav from "./build-compact-nav"
import BuildBodyStep from "./build-body-step"
import BuildDriveStep from "./build-drive-step"
import BuildGradeStep from "./build-grade-step"
import BuildColorStep from "./build-color-step"
import BuildSummaryStep from "./build-summary-step"

const getnameIfDefault = option => option && get("name", option)
const getShortModel = modelName => (modelName.split(" ")[1] || modelName.split(" ")[0]).toUpperCase()

const stateToParamsMapping = (options, safetyModel = false) => ({
  bodyStyle: getnameIfDefault(options.bodyStyle),
  drivetrain: getnameIfDefault(options.drivetrain),
  grade: getnameIfDefault(options.grade),
  transmission: getnameIfDefault(options.transmission),
  engine: getnameIfDefault(options.engine),
  engineSize: options.engine && get("engineSize", options.engine),
  fuelType: options.engine && get("fuelType", options.engine),
  modelCode: safetyModel ? options.engine && get("safetyModelCode", options.engine) : options.engine && get("modelCode", options.engine),
  color: getnameIfDefault(options.color),
  colorCode: options.color && get("ref", options.color),
  interiorDesign: getnameIfDefault(options.interiorDesign),
  interiorDesignCode: options.interiorDesign && get("ref", options.interiorDesign)
})

export class Configurator extends Component {
  constructor(props) {
    super(props)
    const { options, activeStep } = this.paramsToState(this.props.search.query)
    const newAllData = []
    newAllData.push({}) // for 0 to be nothing
    if (options.bodyStyles.length > 1) {
      newAllData.push({
        name: "Body",
        stepType: "bodyStep",
        heading: props.bodyStyleHeading,
        message: `Choose body ${options.drivetrains.length > 1 ? "& drive " : ""}to see price`,
        radioGroup: [{ groupName: "bodyStyles", itemName: "bodyStyle" }],
        fullWidth: true,
        imageSelector: true,
        finePrint: true,
        prevCTA: false,
        nextCTA: {
          label: "Next Step",
          onClick: () => {
            this.props.gtmEvent({
              gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_BODY_CLICKED_NEXT_BUTTON,
              gtmPayload: {
                modelName: this.state.options.nameplate.name,
                stepNumber: this.state.activeStep
              }
            })
            this.changeStep(1)
          }
        }
      })
    }
    if (options.drivetrains.length > 1) {
      newAllData.push({
        name: "Drive",
        stepType: "driveStep",
        heading: props.drivetrainHeading,
        message: "Choose drive to see price",
        radioGroup: [{ groupName: "drivetrains", itemName: "drivetrain" }],
        configuredVehicle: true,
        prevCTA: {
          label: "Previous",
          onClick: () => {
            this.props.gtmEvent({
              gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_DRIVE_CLICKED_PREVIOUS_BUTTON,
              gtmPayload: {
                modelName: this.state.options.nameplate.name,
                stepNumber: this.state.activeStep
              }
            })
            this.changeStep(-1)
          }
        },
        nextCTA: {
          label: "Next Step",
          onClick: () => {
            this.props.gtmEvent({
              gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_DRIVE_CLICKED_NEXT_BUTTON,
              gtmPayload: {
                modelName: this.state.options.nameplate.name,
                stepNumber: this.state.activeStep
              }
            })
            this.changeStep(1)
          }
        }
      })
    }
    // Grade step is always visible
    newAllData.push({
      name: "Grade",
      stepType: "gradeStep",
      heading: props.gradeHeading,
      radioGroup: [
        { groupName: "grades", itemName: "grade" },
        { groupName: "transmissions", itemName: "transmission" },
        { groupName: "engines", itemName: "engine" }
      ],
      showPrice: true,
      configuredVehicle: true,
      featureList: true,
      finePrint: true,
      specifications: true,
      features: true,
      prevCTA: {
        label: "Previous",
        onClick: () => {
          this.props.gtmEvent({
            gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_GRADE_CLIECKED_PREVIOUS_BUTTON,
            gtmPayload: {
              modelName: this.state.options.nameplate.name,
              stepNumber: this.state.activeStep
            }
          })
          this.changeStep(-1)
        }
      },
      nextCTA: {
        label: "Next Step",
        onClick: () => {
          this.props.gtmEvent({
            gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_GRADE_CLICKED_NEXT_BUTTON,
            gtmPayload: {
              modelName: this.state.options.nameplate.name,
              stepNumber: this.state.activeStep
            }
          })
          this.changeStep(1)
        }
      }
    })
    if (options.colors.length > 1) {
      newAllData.push({
        name: "Color",
        stepType: "colorStep",
        heading: props.colorHeading,
        radioGroup: [
          { groupName: "colors", itemName: "color" },
          { groupName: "interiorDesigns", itemName: "interiorDesign" }
        ],
        showPrice: true,
        colorSelector: true,
        configuredVehicle: true,
        prevCTA: {
          label: "Previous",
          onClick: () => {
            this.props.gtmEvent({
              gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_COLOR_CLICKED_PREVIOUS,
              gtmPayload: {
                modelName: this.state.options.nameplate.name,
                stepNumber: this.state.activeStep
              }
            })
            this.changeStep(-1)
          }
        },
        nextCTA: {
          label: "Next Step",
          onClick: () => {
            this.props.gtmEvent({
              gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_COLOR_CLICKED_NEXT,
              gtmPayload: {
                modelName: this.state.options.nameplate.name,
                stepNumber: this.state.activeStep
              }
            })
            this.changeStep(1)
          }
        }
      })
    }
    newAllData.push({
      name: "Summary",
      stepType: "summaryStep",
      heading: props.summaryHeading,
      summary: true,
      specifications: true,
      features: true,
      showChildren: true,
      prevCTA: {
        label: "Previous",
        onClick: () => {
          this.props.gtmEvent({
            gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_SUMMARY_CLICKED_PREVIOUS,
            gtmPayload: {
              modelName: this.state.options.nameplate.name,
              stepNumber: this.state.activeStep
            }
          })
          this.changeStep(-1)
        }
      },
      lastCTA: {
        label: props.finalCta.label,
        href: props.finalCta.href,
        external: props.finalCta.external
      }
    })
    this.state = {
      activeStep: activeStep || 1,
      loading: true,
      options: options,
      selectColorInterior: false,
      newAllData: newAllData,
      form: {
        safetyModel: {
          validations: {
          }
        }
      },
      isModalOpen: false,
      colorStepVisited: false
    }
  }

  componentDidMount() {
    this.props.price.setOptions({
      mapping: temp => ({
        nameplate: temp.nameplate.name,
        bodystyle: temp.bodyStyle.name,
        drivetrain: temp.drivetrain.name,
        grade: temp.grade.name,
        transmission: temp.transmission.name,
        engineSize: temp.engine.engineSize,
        fueltype: temp.engine.fuelType,
        modelCode: temp.engine.modelCode,
        color: temp.color.ref,
        interior: temp.interiorDesign.ref
      }),
      fetchURL: this.props.priceApiEndpoint
    })
    this.props.specifications.setOptions({
      mapping: temp => ({
        nameplate: temp.nameplate.name,
        bodystyle: temp.bodyStyle.name,
        grade: temp.grade.name,
        drivetrain: temp.drivetrain.name,
        engineSize: temp.engine.engineSize,
        fueltype: temp.engine.fuelType,
        modelCode: temp.engine.modelCode,
        transmission: temp.transmission.name,
        featured: true
      }),
      fetchURL: this.props.specsApiEndpoint,
      featured: true
    })
    setTimeout(() => {
      this.props.gtmEvent({
        gtmID: gtmBuildToolActionTypes.BUILDTOOL_LOAD_CONFIG,
        gtmPayload: {
          modelName: this.state.options.nameplate.name,
          stepNumber: this.state.activeStep,
          stepName: this.state.newAllData[this.state.activeStep].name,
          bodyName: this.state.options.bodyStyle.name,
          driveName: this.state.options.drivetrain.name,
          gradeName: this.state.options.grade.name,
          colorName: this.state.options.color.name,
          interiorName: this.state.options.interiorDesign.name
        }
      })
      this.updateData()
      this.setState({ loading: false }, () => {
        scrollToNode(this.node)
      })
    }, 100)
    exposeGlobal("getConfiguredVehicle", () => {
      const { options } = this.state
      const {
        bodyStyle,
        color,
        drivetrain,
        engine,
        grade,
        interiorDesign,
        nameplate,
        transmission
      } = options
      return {
        step: this.state.activeStep,
        bodyStyle,
        color,
        drivetrain,
        engine,
        grade,
        interiorDesign,
        nameplate,
        transmission,
        price: this.props.price.data[0].price,
        stepName: this.state.newAllData[this.state.activeStep].heading || false,
        summary: this.state.newAllData[this.state.activeStep].summary || false
      }
    })
  }

  componentDidUpdate(prevProps, prevState) {
    const { compact } = this.props
    const { activeStep, options, form: { safetyModel } } = this.state

    if ((prevState.activeStep && prevState.activeStep !== activeStep) || (prevProps.compact && prevProps.compact !== compact)) {
      if (safetyModel.value) {
        formSetDefaultValues(this, {
          safetyModel: safetyModel.value
        })
      }
    }


    if (
      activeStep === 2 &&
      (prevState.options.engine && prevState.options.engine.name !== options.engine.name ||
        prevState.options.grade && prevState.options.grade.name !== options.grade.name ||
        prevState.options.transmission && prevState.options.transmission.name !== options.transmission.name)
      ) {
        formSetDefaultValues(this, {
          safetyModel: options.engine.modelCode === options.engine.safetyModelCode
        })
    }
  }

  setStep = (index) => {
    if (index > 0 && index <= this.state.newAllData.length) {
      this.setState({ activeStep: index }, () => {
        scrollToNode(this.node)
        this.updateData()
        this.checkColorStepVisitingStatus()
      })
    }
  }

  checkColorStepVisitingStatus = () => {
    if (this.state.newAllData[this.state.activeStep].stepType === "colorStep") {
      this.setState({ colorStepVisited: true})
    }
  }

  changeStep = (change) => {
    this.setState(
      {
        activeStep: this.state.activeStep + change
      },
      () => {
        scrollToNode(this.node)
        this.updateData()
        this.checkColorStepVisitingStatus()
      }
    )
  }

  handleChangeOption = (option) => {
    this.setState(
      {
        options: parseKoalaData(this.props.data, {
          newSelection: option,
          stateOptions: this.state.options
        },
        this.state.colorStepVisited
        )
      },
      () => {
        this.handleGTMChange(option)
        this.pushHistory()
        this.updatePrice()
        this.updateSpecifications()
      }
    )
  }

  handleGTMChange = (option) => {
    switch (option.type) {
      case "bodyStyle":
        this.props.gtmEvent({
          gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_BODY_CHANGE,
          gtmPayload: {
            modelName: this.state.options.nameplate.name,
            stepNumber: this.state.activeStep,
            bodyName: option.name
          }
        })
        break
      case "drivetrain":
        this.props.gtmEvent({
          gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_DRIVE_CHANGE,
          gtmPayload: {
            modelName: this.state.options.nameplate.name,
            stepNumber: this.state.activeStep,
            driveName: option.name
          }
        })
        break
      case "grade":
      case "transmission":
      case "engine":
        this.props.gtmEvent({
          gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_GRADE_CHANGE,
          gtmPayload: {
            modelName: this.state.options.nameplate.name,
            stepNumber: this.state.activeStep,
            gradeName: `${this.state.options.grade.name} ${this.state.options.transmission
              .name} ${this.state.options.engine.name}`
          }
        })
        break
      case "color":
        this.props.gtmEvent({
          gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_COLOR_CHANGE,
          gtmPayload: {
            modelName: this.state.options.nameplate.name,
            stepNumber: this.state.activeStep,
            colorName: option.name
          }
        })
        break
      case "interiorDesign":
        this.props.gtmEvent({
          gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_COLOR_CHANGE_INTERIOR,
          gtmPayload: {
            modelName: this.state.options.nameplate.name,
            stepNumber: this.state.activeStep,
            interiorName: option.name
          }
        })
        break

      default:
        break
    }
  }

  handleChangeInterior = (newIndex) => {
    this.props.gtmEvent({
      gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_COLOR_CLICKED_INTERIOR,
      gtmPayload: {
        modelName: this.state.options.nameplate.name,
        stepNumber: this.state.activeStep,
        typeName: newIndex === 1 ? "INTERIOR" : "EXTERIOR"
      }
    })
    this.setState(
      {
        selectColorInterior: newIndex
      },
      () => {
        this.pushHistory()
        this.updatePrice()
      }
    )
  }

  updateData() {
    this.pushHistory()
    this.updateSpecifications()
    this.updatePrice()
    this.updateVehicleInLocalStorage()
  }

  pushHistory = () => {
    const step = this.state.activeStep
    this.props.search.pushParam({
      ...stateToParamsMapping(this.state.options, this.state.form.safetyModel.value),
      step: step && step !== 1 && step
    })
  }

  updateSpecifications = () => {
    const { options, form: { safetyModel } } = this.state
    this.props.specifications.update({
      ...options,
      engine: {
        ...options.engine,
        modelCode: safetyModel.value ? options.engine.safetyModelCode : options.engine.modelCode
      }
    })
  }

  updatePrice = () => {
    const { price } = this.props
    price.update(this.state.options)
  }

  updateVehicleInLocalStorage() {
    const { localVehicle } = this.props
    const { options, form } = this.state
    localVehicle.updateVehicle(getnameIfDefault(options.nameplate), stateToParamsMapping(options, form.safetyModel.value))
  }

  paramsToState = (newParams) => {
    let options = null
    let activeStep = 1
    if (newParams) {
      Object.keys(newParams).forEach((key) => {
        const value = newParams[key]
        if (key === "step") {
          activeStep = Number(value)
        } else {
          options = parseKoalaData(this.props.data, {
            newSelection: { type: key, name: value },
            stateOptions: options
          })
        }
      })
    }
    options = options || parseKoalaData(this.props.data)
    return { options, activeStep }
  }

  safetyModel(summary = false) {
    const { safetyModelExcess, safetyModelBlurb, safetyModelDisclaimer } = this.props
    const price = ((this.props.price || {}).data || [])[0] || {}
    const included = price && price.safetyModelPrice && price.price === price.safetyModelPrice

    return (
      <div style={styles.feature(summary)}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <InputCheckBox
            name="safetyModel"
            label="Vision Technology"
            checked={this.props.isInitialSafetyModel || included}
            disabled={included}
            {...fromLinkInputs(this, "safetyModel", () => {
              this.updateData()
            })}
          />
          <span
            onClick={() => {
              this.setState({ isModalOpen: true })
            }}>
            <InfoIcon style={{
              height: "16px",
              width: "16px",
              fill: "#999999",
              paddingLeft: "6px",
              cursor: "pointer",
              verticalAlign: "middle"
            }} />
          </span>
        </div>
        <div>{
          included ?
          <label>Included as standard</label> :
          safetyModelExcess &&
          safetyModelDisclaimer &&
          <Caption>
            <InlineDisclaimerBlock
              message={safetyModelDisclaimer}
              disclaimerStyling={{
                fontSize: "16px",
                top: "0.3em"
              }}>
              +${safetyModelExcess.toLocaleString()}
            </InlineDisclaimerBlock>
          </Caption>
        }</div>
        {
          this.state.isModalOpen &&
          <Modal
            width="600px"
            handleClose={() => {
              this.setState({ isModalOpen: false })
            }}>
            <Content xmlString={safetyModelBlurb} textAlign="left" />
            <Button
              styleName="black"
              onClick={() => {
                this.setState({ isModalOpen: false })
              }}>
              Close
                </Button>
          </Modal>
        }
      </div>
    )
  }

  render() {
    const { backgroundColor, safetyModelExcess, finance, financeOptionText } = this.props

    return (
      <div
        ref={(node) => {
          this.node = node
        }}>
        {this.state.loading && (
          <BrowserHeightSection>
            <Loading />
          </BrowserHeightSection>
        )}
        {!this.state.loading && (
          <div style={styles.base(backgroundColor,50)} data-test="configurator">
            {this.props.compact && (
              <BuildCompactNav
                active={this.state.activeStep}
                title={this.state.newAllData[this.state.activeStep].heading}
                back={() => this.changeStep(-1)}
                reload={() =>
                  this.setState({ options: parseKoalaData(this.props.data) }, () => {
                    this.setStep(1)
                  })} />
            )}
            <div style={styles.container}>
              {!this.props.compact && (
                <div style={styles.dots} data-test="configurator-step-counter">
                  <StepCounter
                    steps={this.state.newAllData.filter((item, index) => index !== 0)}
                    active={this.state.activeStep}
                    setStep={(index) => {
                      this.setStep(index)
                      setTimeout(() => {
                        // Somehow GTM for sidenav is not firing on tbalet
                        // I added this timeout for fixing that issue
                        this.props.gtmEvent({
                          gtmID: gtmBuildToolActionTypes.BUILDTOOL_CLICKED_SIDE_NAV,
                          gtmPayload: {
                            modelName: this.state.options.nameplate.name,
                            stepNumber: this.state.activeStep,
                            stepName: this.state.newAllData[this.state.activeStep].name,
                            newStepName: this.state.newAllData[index].name
                          }
                        })
                      }, 100)
                    }} />
                </div>
              )}
              <div>
                {this.state.newAllData[this.state.activeStep].stepType === "bodyStep" && (
                  <BuildBodyStep
                    stepData={this.state.newAllData[this.state.activeStep]}
                    compact={this.props.compact}
                    price={this.props.price}
                    fullSpecCta={this.props.fullSpecCta}
                    mainHeading={this.props.mainHeading}
                    activeStep={this.state.activeStep}
                    search={this.props.search}
                    options={this.state.options}
                    handleChangeOption={this.handleChangeOption} />
                )}
                {this.state.newAllData[this.state.activeStep].stepType === "driveStep" && (
                  <BuildDriveStep
                    stepData={this.state.newAllData[this.state.activeStep]}
                    compact={this.props.compact}
                    price={this.props.price}
                    fullSpecCta={this.props.fullSpecCta}
                    mainHeading={this.props.mainHeading}
                    activeStep={this.state.activeStep}
                    search={this.props.search}
                    options={this.state.options}
                    handleChangeOption={this.handleChangeOption} />
                )}
                {this.state.newAllData[this.state.activeStep].stepType === "gradeStep" && (
                  <BuildGradeStep
                    price={this.props.price}
                    stepData={this.state.newAllData[this.state.activeStep]}
                    compact={this.props.compact}
                    fullSpecCta={this.props.fullSpecCta}
                    mainHeading={this.props.mainHeading}
                    activeStep={this.state.activeStep}
                    specifications={this.props.specifications}
                    search={this.props.search}
                    options={this.state.options}
                    handleChangeOption={this.handleChangeOption}
                    safetyModel={this.safetyModel()}
                    includeSafetyModel={this.state.form.safetyModel.value}
                    finance={finance} />
                )}
                {this.state.newAllData[this.state.activeStep].stepType === "colorStep" && (
                  <BuildColorStep
                    stepData={this.state.newAllData[this.state.activeStep]}
                    compact={this.props.compact}
                    display360={this.props.display360}
                    price={this.props.price}
                    fullSpecCta={this.props.fullSpecCta}
                    mainHeading={this.props.mainHeading}
                    activeStep={this.state.activeStep}
                    selectColorInterior={this.state.selectColorInterior}
                    handleChangeInterior={this.handleChangeInterior}
                    search={this.props.search}
                    options={this.state.options}
                    handleChangeOption={this.handleChangeOption} />
                )}
                {this.state.newAllData[this.state.activeStep].stepType === "summaryStep" && (
                  <BuildSummaryStep
                    allData={this.state.newAllData}
                    stepData={this.state.newAllData[this.state.activeStep]}
                    compact={this.props.compact}
                    mainHeading={this.props.mainHeading}
                    price={this.props.price}
                    disableSavePrompt={this.props.disableSavePrompt}
                    delayToShowSavePrompt={this.props.delayToShowSavePrompt}
                    fullSpecCta={this.props.fullSpecCta}
                    hideLinks={this.props.hideLinks}
                    activeStep={this.state.activeStep}
                    safetyModel={this.safetyModel(true)}
                    safetyModelExcess={safetyModelExcess}
                    selectColorInterior={this.state.selectColorInterior}
                    savedBuildEndPoint={this.props.savedBuildEndPoint}
                    sharedBuildEndPoint={this.props.sharedBuildEndPoint}
                    search={this.props.search}
                    setStep={(index) => {
                      this.props.gtmEvent({
                        gtmID: gtmBuildToolActionTypes.BUILDTOOL_STEP_SUMMARY_CLICKED_EDIT_BUTTON,
                        gtmPayload: {
                          modelName: this.state.options.nameplate.name,
                          stepNumber: this.state.activeStep,
                          newStepNumber: index
                        }
                      })
                      this.setStep(index)
                    }}
                    specifications={this.props.specifications}
                    includeSafetyModel={this.state.form.safetyModel.value}
                    finance={finance}
                    financeOptionText={financeOptionText}
                    gtmEvents={{
                      collapse: [
                        {
                          func: this.props.gtmEvent,
                          args: [
                            {
                              gtmID:
                                gtmBuildToolActionTypes.BUILDTOOL_STEP_SUMMARY_TOGGLE_FEATURE_SPECIFICATIONS,
                              gtmPayload: {
                                modelName: this.state.options.nameplate.name,
                                stepNumber: this.state.activeStep,
                                newStatusName: "collapse"
                              }
                            }
                          ]
                        }
                      ],
                      expand: [
                        {
                          func: this.props.gtmEvent,
                          args: [
                            {
                              gtmID:
                                gtmBuildToolActionTypes.BUILDTOOL_STEP_SUMMARY_TOGGLE_FEATURE_SPECIFICATIONS,
                              gtmPayload: {
                                modelName: this.state.options.nameplate.name,
                                stepNumber: this.state.activeStep,
                                newStatusName: "expand"
                              }
                            }
                          ]
                        }
                      ]
                    }}
                    gtmEventsDisclaimers={[
                      {
                        func: this.props.gtmEvent,
                        args: [
                          {
                            gtmID:
                              gtmBuildToolActionTypes.BUILDTOOL_CLICKED_FEATURE_SPECIFICATION_DISCLAIMER,
                            gtmPayload: {
                              modelName: this.state.options.nameplate.name,
                              stepNumber: this.state.activeStep
                            }
                          }
                        ]
                      }
                    ]}
                    gtmEventPriceDisclaimer={[
                      {
                        func: this.props.gtmEvent,
                        args: [
                          {
                            gtmID: gtmBuildToolActionTypes.BUILDTOOL_CLICKED_PRICE_DISCLAIMER,
                            gtmPayload: {
                              modelName: this.state.options.nameplate.name,
                              stepNumber: this.state.activeStep,
                              stepName: this.state.newAllData[this.state.activeStep].name
                            }
                          }
                        ]
                      }
                    ]}
                    gtmEventsFullSpecLink={[
                      {
                        func: this.props.gtmEvent,
                        args: [
                          {
                            gtmID:
                              gtmBuildToolActionTypes.BUILDTOOL_CLICKED_FULL_SPECIFICATION_LIST,
                            gtmPayload: {
                              modelName: this.state.options.nameplate.name,
                              stepNumber: this.state.activeStep
                            }
                          }
                        ]
                      }
                    ]}
                    options={this.state.options}
                    testDriveUrl={this.props.finalCta.href}
                    handleChangeOption={this.handleChangeOption}
                    gtmEvent={this.props.gtmEvent}
                    gtmBuildToolActionTypes={gtmBuildToolActionTypes} />
                )}
              </div>
            </div>
            <div>
              {this.state.newAllData[this.state.activeStep].showChildren && (
                <div>{childParser(this.props)}</div>
              )}
            </div>
            <SingularityBar
              nameplate={this.state.options.nameplate.name}
              grade={this.state.options.grade.name}
              compact={this.props.compact}
              drive={this.state.options.drivetrains.length >= 2}
              price={this.props.price}
              includeSafetyModel={this.state.form.safetyModel.value}
              src={this.state.options.color.imageSrc}
              hidden={!this.state.newAllData[this.state.activeStep].showPrice}
              gtmEvents={[
                {
                  func: this.props.gtmEvent,
                  args: [
                    {
                      gtmID: gtmBuildToolActionTypes.BUILDTOOL_CLICKED_PRICE_DISCLAIMER,
                      gtmPayload: {
                        modelName: this.state.options.nameplate.name,
                        stepNumber: this.state.activeStep,
                        stepName: this.state.newAllData[this.state.activeStep].name
                      }
                    }
                  ]
                }
              ]}
              disclaimer={!this.props.disclaimer}
              summary={this.state.newAllData[this.state.activeStep].stepType === "summaryStep"}>
              {this.props.backLink &&
                this.state.activeStep === 1 &&
                !this.props.compact && (
                  <Button
                    label="Previous"
                    href={this.props.backLink}
                    styleName="blackOutlined"
                    display="inline" />
                )}
              {this.state.activeStep !== 1 &&
                this.state.newAllData[this.state.activeStep].prevCTA &&
                !!this.state.activeStep &&
                !this.props.compact && (
                  <Button
                    {...this.state.newAllData[this.state.activeStep].prevCTA}
                    styleName="blackOutlined"
                    display="inline" />
                )}
              {this.state.newAllData[this.state.activeStep].nextCTA && (
                <Button
                  {...this.state.newAllData[this.state.activeStep].nextCTA}
                  display="inline"
                  margin="0" />
              )}
              {this.state.newAllData[this.state.activeStep].lastCTA && (
                <Button
                  {...this.state.newAllData[this.state.activeStep].lastCTA}
                  display="inline"
                  margin="0"
                  gtm={{
                    action: `Build "${getShortModel(this.state.options.nameplate.name)}" - Step ${this.state.activeStep} SUMMARY - Clicked "BOOK A TEST DRIVE"`,
                    category:`Build Tool "${getShortModel(this.state.options.nameplate.name)}"`,
                  }}
                  />
              )}
            </SingularityBar>
          </div>
        )}
      </div>
    )
  }
}

export default withRadium(
  SearchParams({
    trackParams: ["step"],
    exposeParams: [
      "step",
      "bodyStyle",
      "drivetrain",
      "grade",
      "transmission",
      "engine",
      "color",
      "interiorDesign"
    ]
  }),
  gtmEvent(),
  DetectCompact(),
  LocalVehicle(),
  Price({ expose: "price" }),
  Specs({ expose: "specifications" })
)(Configurator)

Configurator.propTypes = {
  mainHeading: PropTypes.string,
  bodyStyleHeading: PropTypes.string,
  drivetrainHeading: PropTypes.string,
  gradeHeading: PropTypes.string,
  colorHeading: PropTypes.string,
  finalCta: PropTypes.object,
  backLink: PropTypes.string,
  priceApiEndpoint: PropTypes.string,
  specsApiEndpoint: PropTypes.string,
  summaryHeading: PropTypes.string,
  fullSpecCta: PropTypes.object,
  data: PropTypes.object,
  gtmEvent: PropTypes.func,
  compact: PropTypes.bool,
  localVehicle: PropTypes.shape({
    updateVehicle: PropTypes.func
  }),
  specifications: PropTypes.shape({
    data: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    update: PropTypes.func,
    setOptions: PropTypes.func
  }),
  price: PropTypes.shape({
    data: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
    update: PropTypes.func,
    setOptions: PropTypes.func
  }),
  search: PropTypes.shape({
    query: PropTypes.object,
    pushParam: PropTypes.func,
    queryString: PropTypes.string
  })
}

Configurator.defaultProps = {
  mainHeading: "",
  bodyStyleHeading: "",
  drivetrainHeading: "",
  gradeHeading: "",
  colorHeading: "",
  finalCta: {},
  fullSpecCta: {},
  priceApiEndpoint: "",
  hideLinks: false,
  specsApiEndpoint: "",
  summaryHeading: "",
  data: {},
  specifications: { data: false, update: noop, setOptions: noop },
  price: { data: false, update: noop, setOptions: noop }
}

const styles = {
  base: (backgroundColor,singularityBarHeight) => ({
    background: backgroundColor || "#f5f5f5",
    position: "relative",
    textAlign: "center",
    maxWidth: "100vw",
    overflow: "hidden",
    minHeight: `calc(100vh - ${singularityBarHeight}px)`
  }),
  dots: {
    position: "fixed",
    top: "50%",
    left: "1em",
    transform: "translateY(-50%)",
    zIndex: 10
  },
  container: {
    flex: "1 1 100%",
    margin: "0 auto"
  },
  feature: summary => ({
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: !summary && "1px solid #e7e7e7",
    minHeight: !summary && "44px"
  })
}



// WEBPACK FOOTER //
// ./src/containers/build/index.jsx